import React, { useEffect, useMemo, useState } from "react";
import { View, Switch } from "react-native";
import CustomButton from "../../components/common/general/CustomButton/CustomButton";
import CustomText from "../../components/common/general/CustomText/CustomText";
import CustomSpacing from "../../components/common/layout/CustomSpacing/CustomSpacing";
import {
  useCreateSamlConfigMutation,
  useDeleteSamlConfigMutation,
  useGetSamlConfigByTenantQuery,
  useUpdateSamlConfigMutation,
  GetSamlConfigByTenantDocument,
  SamlConfigUpdateInput,
} from "../../graphql/generated/graphql";
import { Controller, useForm } from "react-hook-form";
import CustomTextInput from "../../components/common/general/CustomTextInput/CustomTextInput";

interface SamlConfigProps {
  tenantId: string;
  tenantDomain: string;
}

function SamlConfig({ tenantId, tenantDomain }: SamlConfigProps) {
  const { data, loading, error } = useGetSamlConfigByTenantQuery({
    variables: {
      tenantId,
    },
  });

  const [createSamlConfig, { loading: createSamlConfigLoading, error: createSamlConfigError }] =
    useCreateSamlConfigMutation();
  const [deleteSamlConfig, { loading: deleteSamlConfigLoading, error: deleteSamlConfigError }] =
    useDeleteSamlConfigMutation();
  const [updateSamlConfig, { loading: updateSamlConfigLoading, error: updateSamlConfigError }] =
    useUpdateSamlConfigMutation();

  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const hasConfig = !!data?.getSamlConfigByTenant?.data?.id;
  const samlConfigId = data?.getSamlConfigByTenant?.data?.id;

  const defaultFormValues: SamlConfigUpdateInput = useMemo(
    () => ({
      entryPoint: "",
      issuer: "",
      cert: "",
      callbackUrl: "",
      emailAttribute: "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress",
      firstNameAttribute: "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname",
      lastNameAttribute: "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname",
      signRequest: false,
      signatureAlgorithm: "sha256",
    }),
    []
  );

  const { control, handleSubmit, watch, reset } = useForm<SamlConfigUpdateInput>({
    defaultValues: defaultFormValues,
  });

  const requiredEntryPoint = watch("entryPoint");
  const requiredIssuer = watch("issuer");
  const requiredCert = watch("cert");
  const requiredCallbackUrl = watch("callbackUrl");
  const isSubmitDisabled = !requiredEntryPoint || !requiredIssuer || !requiredCert || !requiredCallbackUrl;

  const handleCreateSamlConfig = () => {
    setIsCreating(true);
  };

  const handleDeleteSamlConfig = () => {
    if (!samlConfigId) return;
    deleteSamlConfig({
      variables: { id: samlConfigId },
      refetchQueries: [{ query: GetSamlConfigByTenantDocument, variables: { tenantId } }],
    });
  };

  const handleUpdateSamlConfig = () => {
    const existing = data?.getSamlConfigByTenant?.data;
    if (existing) {
      reset({
        entryPoint: existing.entryPoint || "",
        issuer: existing.issuer || "",
        cert: existing.cert || "",
        callbackUrl: existing.callbackUrl || "",
        emailAttribute: existing.emailAttribute || "",
        firstNameAttribute: existing.firstNameAttribute || "",
        lastNameAttribute: existing.lastNameAttribute || "",
        signRequest: !!existing.signRequest,
        signatureAlgorithm: existing.signatureAlgorithm || "",
      });
    }
    setIsEditing(true);
  };

  const onSubmit = async (values: SamlConfigUpdateInput) => {
    if (isEditing && samlConfigId) {
      await updateSamlConfig({
        variables: {
          id: samlConfigId,
          input: {
            entryPoint: values.entryPoint,
            issuer: values.issuer,
            cert: values.cert,
            callbackUrl: values.callbackUrl,
            emailAttribute: values.emailAttribute,
            firstNameAttribute: values.firstNameAttribute,
            lastNameAttribute: values.lastNameAttribute,
            signRequest: values.signRequest,
            signatureAlgorithm: values.signatureAlgorithm,
          },
        },
        refetchQueries: [{ query: GetSamlConfigByTenantDocument, variables: { tenantId } }],
      });
      setIsEditing(false);
      return;
    }

    await createSamlConfig({
      variables: {
        input: {
          entryPoint: values.entryPoint || "",
          issuer: values.issuer || "",
          cert: values.cert || "",
          callbackUrl: values.callbackUrl || "",
          emailAttribute: values.emailAttribute || undefined,
          firstNameAttribute: values.firstNameAttribute || undefined,
          lastNameAttribute: values.lastNameAttribute || undefined,
          signRequest: typeof values.signRequest === "boolean" ? values.signRequest : undefined,
          signatureAlgorithm: values.signatureAlgorithm || undefined,
          tenant: {
            connect: {
              where: {
                node: { id: tenantId },
              },
            },
          },
        },
      },
      refetchQueries: [{ query: GetSamlConfigByTenantDocument, variables: { tenantId } }],
    });
    setIsCreating(false);
  };

  return (
    <View>
      <CustomText
        role="header"
        text={`${tenantDomain} settings - SAML config`}
        size="xxl"
        weight="ultraBold"
        useTranslationText={false}
      />
      <CustomSpacing size="l" />
      {!loading && !isCreating && !isEditing && hasConfig && (
        <View>
          <CustomText text="Current SAML configuration" size="m" weight="ultraBold" useTranslationText={false} />
          <CustomSpacing size="s" />
          <CustomText
            text={`Entry Point: ${data?.getSamlConfigByTenant?.data?.entryPoint || "-"}`}
            size="s"
            useTranslationText={false}
          />
          <CustomSpacing size="xs" />
          <CustomText
            text={`Issuer: ${data?.getSamlConfigByTenant?.data?.issuer || "-"}`}
            size="s"
            useTranslationText={false}
          />
          <CustomSpacing size="xs" />
          <CustomText
            text={`Certificate: ${
              data?.getSamlConfigByTenant?.data?.cert
                ? `${data.getSamlConfigByTenant.data.cert.slice(0, 40)}... (${data.getSamlConfigByTenant.data.cert.length} chars)`
                : "-"
            }`}
            size="s"
            useTranslationText={false}
          />
          <CustomSpacing size="xs" />
          <CustomText
            text={`Callback URL: ${data?.getSamlConfigByTenant?.data?.callbackUrl || "-"}`}
            size="s"
            useTranslationText={false}
          />
          <CustomSpacing size="xs" />
          <CustomText
            text={`Email Attribute: ${data?.getSamlConfigByTenant?.data?.emailAttribute || "-"}`}
            size="s"
            useTranslationText={false}
          />
          <CustomSpacing size="xs" />
          <CustomText
            text={`First Name Attribute: ${data?.getSamlConfigByTenant?.data?.firstNameAttribute || "-"}`}
            size="s"
            useTranslationText={false}
          />
          <CustomSpacing size="xs" />
          <CustomText
            text={`Last Name Attribute: ${data?.getSamlConfigByTenant?.data?.lastNameAttribute || "-"}`}
            size="s"
            useTranslationText={false}
          />
          <CustomSpacing size="xs" />
          <CustomText
            text={`Sign Request: ${
              data?.getSamlConfigByTenant?.data?.signRequest === true
                ? "true"
                : data?.getSamlConfigByTenant?.data?.signRequest === false
                  ? "false"
                  : "-"
            }`}
            size="s"
            useTranslationText={false}
          />
          <CustomSpacing size="xs" />
          <CustomText
            text={`Signature Algorithm: ${data?.getSamlConfigByTenant?.data?.signatureAlgorithm || "-"}`}
            size="s"
            useTranslationText={false}
          />
          <CustomSpacing size="m" />
        </View>
      )}
      {!isCreating && !isEditing && (
        <View style={{ flexDirection: "row", justifyContent: "flex-start", alignItems: "flex-start", gap: 10 }}>
          {!hasConfig && (
            <CustomButton
              text="Create SAML config"
              onPress={handleCreateSamlConfig}
              useTranslationText={false}
              styleType="lightGreen"
            />
          )}
          {hasConfig && (
            <>
              <CustomButton
                text="Delete SAML config"
                onPress={handleDeleteSamlConfig}
                useTranslationText={false}
                styleType="lightGreen"
              />
              <CustomButton
                text="Edit SAML config"
                onPress={handleUpdateSamlConfig}
                useTranslationText={false}
                styleType="lightGreen"
              />
            </>
          )}
        </View>
      )}

      {(isCreating || isEditing) && (
        <View style={{ maxWidth: 700 }}>
          <CustomSpacing size="m" />

          <CustomText text="Entry Point" size="s" useTranslationText={false} />
          <CustomSpacing size="xs" />
          <Controller
            control={control}
            name="entryPoint"
            render={({ field: { onChange, onBlur, value } }) => (
              <CustomTextInput
                value={value as string}
                onChangeText={onChange}
                onBlur={onBlur}
                placeholder="https://idp.example.com/sso"
              />
            )}
          />
          <CustomSpacing size="s" />

          <CustomText text="Issuer" size="s" useTranslationText={false} />
          <CustomSpacing size="xs" />
          <Controller
            control={control}
            name="issuer"
            render={({ field: { onChange, onBlur, value } }) => (
              <CustomTextInput value={value as string} onChangeText={onChange} onBlur={onBlur} placeholder="issuer" />
            )}
          />
          <CustomSpacing size="s" />

          <CustomText text="Certificate (x509)" size="s" useTranslationText={false} />
          <CustomSpacing size="xs" />
          <Controller
            control={control}
            name="cert"
            render={({ field: { onChange, onBlur, value } }) => (
              <CustomTextInput
                value={value as string}
                onChangeText={onChange}
                onBlur={onBlur}
                placeholder="-----BEGIN CERTIFICATE-----..."
                multiline
              />
            )}
          />
          <CustomSpacing size="s" />

          <CustomText text="Callback URL" size="s" useTranslationText={false} />
          <CustomSpacing size="xs" />
          <Controller
            control={control}
            name="callbackUrl"
            render={({ field: { onChange, onBlur, value } }) => (
              <CustomTextInput
                value={value as string}
                onChangeText={onChange}
                onBlur={onBlur}
                placeholder="https://app.example.com/api/auth/saml/callback"
              />
            )}
          />
          <CustomSpacing size="s" />

          <CustomText text="Email attribute" size="s" useTranslationText={false} />
          <CustomSpacing size="xs" />
          <Controller
            control={control}
            name="emailAttribute"
            render={({ field: { onChange, onBlur, value } }) => (
              <CustomTextInput value={(value as string) || ""} onChangeText={onChange} onBlur={onBlur} />
            )}
          />
          <CustomSpacing size="s" />

          <CustomText text="First name attribute" size="s" useTranslationText={false} />
          <CustomSpacing size="xs" />
          <Controller
            control={control}
            name="firstNameAttribute"
            render={({ field: { onChange, onBlur, value } }) => (
              <CustomTextInput value={(value as string) || ""} onChangeText={onChange} onBlur={onBlur} />
            )}
          />
          <CustomSpacing size="s" />

          <CustomText text="Last name attribute" size="s" useTranslationText={false} />
          <CustomSpacing size="xs" />
          <Controller
            control={control}
            name="lastNameAttribute"
            render={({ field: { onChange, onBlur, value } }) => (
              <CustomTextInput value={(value as string) || ""} onChangeText={onChange} onBlur={onBlur} />
            )}
          />
          <CustomSpacing size="s" />

          <CustomText text="Sign request" size="s" useTranslationText={false} />
          <CustomSpacing size="xs" />
          <Controller
            control={control}
            name="signRequest"
            render={({ field: { onChange, value } }) => <Switch value={!!value} onValueChange={onChange} />}
          />
          <CustomSpacing size="s" />

          <CustomText text="Signature algorithm" size="s" useTranslationText={false} />
          <CustomSpacing size="xs" />
          <Controller
            control={control}
            name="signatureAlgorithm"
            render={({ field: { onChange, onBlur, value } }) => (
              <CustomTextInput
                value={(value as string) || ""}
                onChangeText={onChange}
                onBlur={onBlur}
                placeholder="sha256"
              />
            )}
          />

          <CustomSpacing size="m" />
          <View style={{ flexDirection: "row", gap: 10 }}>
            <CustomButton
              text={isEditing ? "Update" : "Submit"}
              onPress={handleSubmit(onSubmit)}
              useTranslationText={false}
              styleType="lightGreen"
              disabled={(isEditing ? false : isSubmitDisabled) || createSamlConfigLoading || updateSamlConfigLoading}
            />
            <CustomButton
              text="Cancel"
              onPress={() => {
                setIsCreating(false);
                setIsEditing(false);
              }}
              useTranslationText={false}
              styleType="transparent"
            />
          </View>
          <CustomSpacing size="m" />
          {(createSamlConfigError || deleteSamlConfigError || updateSamlConfigError || error) && (
            <CustomText
              text={
                createSamlConfigError?.message ||
                deleteSamlConfigError?.message ||
                updateSamlConfigError?.message ||
                error?.message ||
                ""
              }
              size="s"
              style={{ color: "red" }}
              useTranslationText={false}
            />
          )}
        </View>
      )}
    </View>
  );
}

export default SamlConfig;
